import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { CantonParty } from './cantonModel';

type Palette = ReturnType<typeof useColors>;

/** One stable color per party, so Alice looks the same in every widget of the track. */
export const partyColor = (colors: Palette, party: CantonParty): string => ({
    Alice: colors.accent.purple,
    Bob: colors.rank.master,
    Bank: colors.accent.green,
    Registry: colors.accent.orange,
    Carol: colors.difficulty.medium,
})[party];

export const PartyChip = ({ party, dimmed }: { party: CantonParty; dimmed?: boolean }) => {
    const colors = useColors();
    const color = partyColor(colors, party);
    return (
        <Box component="span" sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            px: '8px',
            py: '2px',
            borderRadius: '999px',
            border: `1px solid ${dimmed ? colors.border.subtle : color}`,
            color: dimmed ? colors.text.muted : colors.text.primary,
            fontSize: '0.8rem',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
        }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dimmed ? colors.text.muted : color }} />
            {party}
        </Box>
    );
};

interface PartyPickerProps {
    parties: CantonParty[];
    selected: CantonParty[];
    onToggle: (party: CantonParty) => void;
    label: string;
}

/** A row of pressable party chips; works for single-select (pass one) or multi-select. */
export const PartyPicker = ({ parties, selected, onToggle, label }: PartyPickerProps) => {
    const colors = useColors();
    return (
        <Box role="group" aria-label={label} sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {parties.map((party) => {
                const active = selected.includes(party);
                const color = partyColor(colors, party);
                return (
                    <ButtonBase
                        key={party}
                        onClick={() => onToggle(party)}
                        aria-pressed={active}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            px: '12px',
                            py: '6px',
                            borderRadius: '999px',
                            border: `1px solid ${active ? color : colors.border.default}`,
                            backgroundColor: active ? colors.bg.surface : 'transparent',
                            color: active ? colors.text.primary : colors.text.secondary,
                            fontWeight: 700,
                            fontSize: '0.86rem',
                            transition: 'all 0.2s ease',
                            '&:focus-visible': { outline: `2px solid ${color}`, outlineOffset: '2px' },
                        }}
                    >
                        <Box component="span" sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: active ? color : 'transparent',
                            border: `2px solid ${color}`,
                        }} />
                        {party}
                    </ButtonBase>
                );
            })}
        </Box>
    );
};

/** A single outcome line: a colored status word followed by an explanation. */
export const Verdict = ({ ok, title, children }: { ok: boolean; title: string; children?: React.ReactNode }) => {
    const colors = useColors();
    return (
        <Box aria-live="polite">
            <Typography sx={{ color: ok ? colors.accent.green : colors.accent.orange, fontWeight: 800, mb: 0.5 }}>
                {title}
            </Typography>
            {children && (
                <Typography component="div" sx={{ color: colors.text.secondary, lineHeight: 1.65, fontSize: '0.9rem' }}>
                    {children}
                </Typography>
            )}
        </Box>
    );
};
